/**
 * ColourMatch Pro · Colour Management System — landing page.
 *
 * This is the permanent home of Painter Projects. Phase 1 (Foundations)
 * ships: project list · empty state · Create New Project modal ·
 * status indicator showing which phase of PRO-CMS-01 we're on.
 *
 * Phase 2+ will replace the list rows with richer project cards (scheme
 * count, last-edited-by, etc.) and add a per-project workspace route at
 * `/pro/cms/projects/:id`. The page structure itself does not change.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, FolderOpen, ArrowRight, Loader2, Image as ImageIcon, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PaymentsWidget from "@/components/pro/PaymentsWidget";
import { useUploadDraft } from "@/contexts/UploadDraftContext";

const PHASE_BLURB = "Every client project is the primary painter workspace — one place to prepare colour concepts, visualisation proposals and colour schedules. Business tools support the project; they never replace it.";

function EmptyState({ onCreate }) {
  return (
    <div
      className="border border-dashed border-brand-linen bg-white p-10 lg:p-14 text-center"
      data-testid="pro-cms-empty-state"
    >
      <div className="w-12 h-12 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mx-auto mb-4">
        <FolderOpen className="w-5 h-5" />
      </div>
      <h3 className="font-serif text-xl text-brand-ink mb-2">No client projects yet</h3>
      <p className="text-sm text-brand-muted max-w-md mx-auto mb-6">
        Start your first client project to create photo-realistic colour concepts,
        professional visualisation proposals, and win more work.
      </p>
      <Button
        onClick={onCreate}
        className="bg-brand-navy text-white hover:bg-brand-teal/90 h-11 px-6"
        data-testid="pro-cms-empty-create-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        Start your first project
      </Button>
    </div>
  );
}

function ProjectRow({ project }) {
  const statusStyles = {
    draft:    "bg-brand-linen/50 text-brand-ink",
    active:   "bg-brand-teal/10 text-brand-teal",
    archived: "bg-brand-ink/10 text-brand-ink/60",
  };
  const badgeClass = statusStyles[project.status] || statusStyles.draft;
  return (
    <Link
      to={`/pro/cms/projects/${project.id}`}
      className="flex items-center justify-between border border-brand-linen bg-white p-5 hover:border-brand-teal/40 hover:bg-brand-linen/10 transition-colors"
      data-testid={`pro-cms-project-row-${project.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1">
          <h3 className="font-serif text-lg text-brand-ink truncate" data-testid="pro-cms-project-name">
            {project.name}
          </h3>
          <span className={`inline-flex items-center px-2 h-5 text-[10px] uppercase tracking-wide font-medium ${badgeClass}`}>
            {project.status}
          </span>
        </div>
        <div className="text-xs text-brand-muted truncate">
          {project.client_name || "No client name"}
          {project.site_address ? ` · ${project.site_address}` : ""}
        </div>
      </div>
      <div className="ml-4 flex items-center gap-2 text-brand-muted text-xs">
        <ArrowRight className="w-4 h-4" />
      </div>
    </Link>
  );
}

// EmptyState label overridden above ("consultation" copy).

function CreateProjectDialog({ open, onOpenChange, onCreated, stagedPhoto, stagedPhotoPreview }) {
  const [name, setName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [siteAddress, setSiteAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName("");
    setClientName("");
    setClientEmail("");
    setSiteAddress("");
    setNotes("");
    setSubmitting(false);
  };

  const handleOpenChange = (next) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim()) {
      toast.error("Please enter a consultation name.");
      return;
    }
    setSubmitting(true);
    try {
      let data;
      if (stagedPhoto) {
        // Multipart path — server creates + attaches atomically. Draft is
        // only cleared by the caller after 2xx.
        const fd = new FormData();
        fd.append("name", name.trim());
        if (clientName.trim()) fd.append("client_name", clientName.trim());
        if (clientEmail.trim()) fd.append("client_email", clientEmail.trim());
        if (siteAddress.trim()) fd.append("site_address", siteAddress.trim());
        if (notes.trim()) fd.append("notes", notes.trim());
        fd.append("photo", stagedPhoto);
        const res = await api.post("/pro/cms/projects", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        data = res.data;
      } else {
        const res = await api.post("/pro/cms/projects", {
          name: name.trim(),
          client_name: clientName.trim() || null,
          client_email: clientEmail.trim() || null,
          site_address: siteAddress.trim() || null,
          notes: notes.trim() || null,
        });
        data = res.data;
      }
      toast.success(stagedPhoto ? "Client project created with source photo" : "Client project created");
      onCreated?.(data);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not create client project.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="pro-cms-create-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-ink">
              {stagedPhoto ? "Create client project with your photo" : "Start a new client project"}
            </DialogTitle>
          </DialogHeader>
          {stagedPhoto && stagedPhotoPreview && (
            <div
              data-testid="pro-cms-create-staged-photo"
              className="mt-3 mb-1 flex items-center gap-3 p-2 rounded-lg bg-brand-sand border border-brand-border"
            >
              <img
                src={stagedPhotoPreview}
                alt="Source photo preview"
                className="w-14 h-14 rounded object-cover flex-shrink-0"
              />
              <div className="text-xs text-brand-muted leading-tight">
                <div className="text-brand-ink font-semibold text-sm">Source photo attached</div>
                <div className="truncate max-w-[220px]">{stagedPhoto.name}</div>
              </div>
            </div>
          )}
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="pro-cms-name" className="text-brand-ink">Project name *</Label>
              <Input
                id="pro-cms-name"
                data-testid="pro-cms-create-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. 42 Ocean Drive · Full repaint"
                maxLength={140}
                autoFocus
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="pro-cms-client" className="text-brand-ink">Client name</Label>
              <Input
                id="pro-cms-client"
                data-testid="pro-cms-create-client"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Optional"
                maxLength={140}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="pro-cms-client-email" className="text-brand-ink">
                Client email
              </Label>
              <Input
                id="pro-cms-client-email"
                type="email"
                data-testid="pro-cms-create-client-email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="Optional — auto-sends the colour schedule when you share a proposal"
                maxLength={254}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="pro-cms-address" className="text-brand-ink">Site address</Label>
              <Input
                id="pro-cms-address"
                data-testid="pro-cms-create-address"
                value={siteAddress}
                onChange={(e) => setSiteAddress(e.target.value)}
                placeholder="Optional"
                maxLength={280}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="pro-cms-notes" className="text-brand-ink">Notes</Label>
              <Textarea
                id="pro-cms-notes"
                data-testid="pro-cms-create-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — quick reminders for yourself"
                maxLength={2000}
                rows={3}
                className="mt-1.5"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
              data-testid="pro-cms-create-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !name.trim()}
              className="bg-brand-navy text-white hover:bg-brand-teal/90"
              data-testid="pro-cms-create-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
              ) : (
                "Create client project"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ProCmsHome() {
  const nav = useNavigate();
  const { draft, previewUrl, hasDraft, clearDraft } = useUploadDraft();
  const [projects, setProjects] = useState(null); // null = loading
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createWithDraft, setCreateWithDraft] = useState(false); // create dialog opened from pill
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachTarget, setAttachTarget] = useState(null); // project row selected for attach
  const [attachReplaceConfirm, setAttachReplaceConfirm] = useState(false);
  const [attachSubmitting, setAttachSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Support `?new=1` deep-link — dashboard "Start a new client project"
  // tile opens the create modal directly instead of dropping the painter
  // on the list first (saves one click on the most-used workflow entry).
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setDialogOpen(true);
      // Strip the query param so a back-navigation doesn't re-open the modal.
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await api.get("/pro/cms/projects");
      setProjects(data.items || []);
      setError(null);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Could not load your client projects.");
      setProjects([]);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const onCreated = async (project) => {
    setProjects((prev) => (prev ? [project, ...prev] : [project]));
    // If this project was created with a staged photo, the server has confirmed
    // it's attached — safe to clear the draft and open the workspace.
    if (createWithDraft && project?.has_source_photo) {
      await clearDraft();
      setCreateWithDraft(false);
      nav(`/pro/cms/projects/${project.id}`);
    }
  };

  const openCreateFresh = () => {
    setCreateWithDraft(false);
    setDialogOpen(true);
  };
  const openCreateWithDraft = () => {
    setCreateWithDraft(true);
    setDialogOpen(true);
  };

  const attachToProject = async (project) => {
    if (!draft) return;
    // If project already has a source photo, require confirmation.
    if (project.has_source_photo && !attachReplaceConfirm) {
      setAttachTarget(project);
      setAttachReplaceConfirm(true);
      return;
    }
    setAttachSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("photo", draft);
      if (project.has_source_photo) fd.append("replace", "true");
      const { data } = await api.post(`/pro/cms/projects/${project.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Server confirmed → safe to clear the draft.
      await clearDraft();
      setAttachOpen(false);
      setAttachTarget(null);
      setAttachReplaceConfirm(false);
      toast.success("Source photo attached");
      // Update local list with new has_source_photo=true.
      setProjects((prev) => (prev || []).map((p) => (p.id === data.id ? data : p)));
      nav(`/pro/cms/projects/${data.id}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        // Backend guard tripped — show confirmation UI.
        setAttachTarget(project);
        setAttachReplaceConfirm(true);
        toast.info("This project already has a source photo. Confirm replacement to overwrite.");
      } else {
        toast.error(formatApiError(err?.response?.data?.detail) || "Could not attach photo. Please try again.");
      }
      setAttachSubmitting(false);
    } finally {
      setAttachSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="pro-cms-home">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline text-brand-teal mb-2">ColourMatch Pro</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-brand-ink" data-testid="pro-cms-heading">
            Client projects
          </h1>
          <p className="text-sm text-brand-muted mt-2 max-w-xl leading-relaxed">
            {PHASE_BLURB}
          </p>
        </div>
        <div className="shrink-0">
          <Button
            onClick={openCreateFresh}
            className="bg-brand-navy text-white hover:bg-brand-teal/90 h-11 px-6"
            data-testid="pro-cms-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New client project
          </Button>
        </div>
      </div>

      {/* Stage 2B (2026-02) · Waiting-image pill.
          Renders only when the painter has a homepage-staged upload
          preserved in UploadDraftContext. Never uploads twice — this
          is the single source. */}
      {hasDraft && (
        <div
          data-testid="pro-cms-waiting-image"
          className="rounded-2xl border border-brand-teal/40 bg-brand-teal/5 p-5 sm:p-6"
        >
          <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Staged source photo"
                className="w-20 h-20 rounded-xl object-cover flex-shrink-0 border border-brand-border bg-white"
                data-testid="pro-cms-waiting-thumb"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal">
                Source photo ready
              </div>
              <h2 className="mt-1 font-serif text-lg text-brand-ink">
                1 uploaded image waiting
              </h2>
              <p className="mt-1 text-xs text-brand-muted leading-relaxed max-w-md truncate">
                {draft?.name || "Your homepage upload is staged and ready to attach to a client project."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  type="button"
                  onClick={openCreateWithDraft}
                  className="bg-brand-navy text-white hover:bg-brand-teal/90 h-10 px-5"
                  data-testid="pro-cms-waiting-create-new"
                >
                  Create New Client Project
                </Button>
                <Button
                  type="button"
                  onClick={() => setAttachOpen(true)}
                  variant="outline"
                  className="h-10 px-5 border-brand-navy text-brand-navy hover:bg-brand-sand"
                  data-testid="pro-cms-waiting-attach-existing"
                >
                  Attach to Existing Project
                </Button>
                <button
                  type="button"
                  onClick={clearDraft}
                  className="text-xs text-brand-muted hover:text-brand-ink px-2 self-center"
                  data-testid="pro-cms-waiting-discard"
                  aria-label="Discard staged photo"
                >
                  Discard photo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      {error && (
        <div className="border border-yellow-300 bg-yellow-50 text-sm text-yellow-800 px-4 py-3" data-testid="pro-cms-error">
          {error}
        </div>
      )}

      {/* Payments awaiting action widget — hides itself when there are no jobs. */}
      <PaymentsWidget />

      {projects === null ? (
        <div className="flex items-center gap-2 text-sm text-brand-muted" data-testid="pro-cms-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading projects…
        </div>
      ) : projects.length === 0 ? (
        <EmptyState onCreate={openCreateFresh} />
      ) : (
        <div className="space-y-3" data-testid="pro-cms-project-list">
          {projects.map((p) => (
            <ProjectRow key={p.id} project={p} />
          ))}
        </div>
      )}

      <CreateProjectDialog
        open={dialogOpen}
        onOpenChange={(next) => {
          if (!next) setCreateWithDraft(false);
          setDialogOpen(next);
        }}
        onCreated={onCreated}
        stagedPhoto={createWithDraft ? draft : null}
        stagedPhotoPreview={createWithDraft ? previewUrl : null}
      />

      {/* Attach-to-existing dialog (Stage 2B) — painter-scoped project list, no cross-painter leakage */}
      <Dialog
        open={attachOpen}
        onOpenChange={(next) => {
          if (!next) {
            setAttachTarget(null);
            setAttachReplaceConfirm(false);
          }
          setAttachOpen(next);
        }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="pro-cms-attach-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-brand-ink">
              {attachReplaceConfirm ? "Replace the existing source photo?" : "Attach to an existing project"}
            </DialogTitle>
          </DialogHeader>

          {attachReplaceConfirm && attachTarget ? (
            <div className="py-3">
              <p className="text-sm text-brand-ink" data-testid="pro-cms-attach-replace-copy">
                <span className="font-semibold">&ldquo;{attachTarget.name}&rdquo;</span> already has a source photo.
                Replacing it will overwrite the current image. This action cannot be undone.
              </p>
              <DialogFooter className="mt-6 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="border-brand-border"
                  onClick={() => { setAttachReplaceConfirm(false); setAttachTarget(null); }}
                  data-testid="pro-cms-attach-replace-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="bg-brand-navy text-white hover:bg-brand-teal/90"
                  disabled={attachSubmitting}
                  onClick={() => attachToProject(attachTarget)}
                  data-testid="pro-cms-attach-replace-confirm"
                >
                  {attachSubmitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Replacing…</>
                  ) : (
                    "Replace source photo"
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="py-2">
              <p className="text-xs text-brand-muted mb-3">
                Choose one of your client projects to attach the staged photo. Only your projects are shown.
              </p>
              <div
                className="max-h-80 overflow-y-auto divide-y divide-brand-border rounded-xl border border-brand-border bg-white"
                data-testid="pro-cms-attach-list"
              >
                {(projects || []).length === 0 && (
                  <div className="p-4 text-sm text-brand-muted" data-testid="pro-cms-attach-empty">
                    You don&rsquo;t have any client projects yet. Create one instead.
                  </div>
                )}
                {(projects || []).map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => attachToProject(p)}
                    disabled={attachSubmitting}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-brand-sand transition-colors disabled:opacity-60"
                    data-testid={`pro-cms-attach-project-${p.id}`}
                  >
                    <span className="w-9 h-9 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0">
                      <ImageIcon size={16} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-brand-ink truncate">{p.name}</span>
                      <span className="block text-xs text-brand-muted truncate">
                        {p.client_name || "No client name"}
                        {p.has_source_photo && " · Already has a source photo"}
                      </span>
                    </span>
                    <ArrowRight size={14} className="text-brand-muted" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
